import React, { useState } from 'react';
import { Bug, ChevronDown, RefreshCw, Play } from 'lucide-react';
import { CampaignFormData, WorkflowStep } from '../types';

interface WorkflowDebugPanelProps {
  formData: CampaignFormData;
  campaignId: string | null;
  currentStep: number;
  steps: WorkflowStep[];
  isProcessing: boolean;
  isComplete: boolean;
  shouldStartAnalysis: boolean;
  onRestartAnalysis: () => void;
  onForceProcessStep: () => void;
}

const WorkflowDebugPanel: React.FC<WorkflowDebugPanelProps> = ({
  formData,
  campaignId,
  currentStep,
  steps,
  isProcessing,
  isComplete,
  shouldStartAnalysis,
  onRestartAnalysis,
  onForceProcessStep
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const debugInfo = {
    campaignId: campaignId || 'NOT SET',
    currentStep,
    totalSteps: steps.length,
    isProcessing,
    isComplete,
    shouldStartAnalysis,
    stepsStatus: steps.map(s => ({ id: s.id, title: s.title, status: s.status })),
    formDataKeys: Object.keys(formData),
    timestamp: new Date().toISOString()
  };

  if (!isExpanded) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-white p-3 rounded-full shadow-lg transition-colors"
          title="Show Debug Panel"
        >
          <Bug className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white border border-gray-300 rounded-lg shadow-xl max-w-md w-96 max-h-96 overflow-y-auto">
      <div className="bg-yellow-500 text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5" />
          <span className="font-medium">Workflow Debug</span>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="hover:bg-yellow-600 p-1 rounded"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Status Summary */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-2">Current Status</h4>
          <div className="text-sm space-y-1">
            <div>Campaign ID: <span className="font-mono text-xs">{debugInfo.campaignId}</span></div>
            <div>Step: {debugInfo.currentStep} / {debugInfo.totalSteps}</div>
            <div>Processing: <span className={isProcessing ? 'text-blue-600' : 'text-gray-600'}>{isProcessing ? 'Yes' : 'No'}</span></div>
            <div>Complete: <span className={isComplete ? 'text-green-600' : 'text-gray-600'}>{isComplete ? 'Yes' : 'No'}</span></div>
            <div>Should Start: <span className={shouldStartAnalysis ? 'text-green-600' : 'text-red-600'}>{shouldStartAnalysis ? 'Yes' : 'No'}</span></div>
          </div>
        </div>

        {/* Steps Status */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-2">Steps Status</h4>
          <div className="text-xs space-y-1 max-h-32 overflow-y-auto">
            {debugInfo.stepsStatus.map(step => (
              <div key={step.id} className="flex justify-between">
                <span className="truncate">{step.id}. {step.title}</span>
                <span className={`ml-2 px-1 rounded text-xs ${
                  step.status === 'completed' ? 'bg-green-100 text-green-800' :
                  step.status === 'loading' ? 'bg-blue-100 text-blue-800' :
                  step.status === 'error' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {step.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onRestartAnalysis}
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1"
          >
            <RefreshCw className="h-4 w-4" />
            Restart
          </button>
          <button
            onClick={onForceProcessStep}
            disabled={isProcessing}
            className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1"
          >
            <Play className="h-4 w-4" />
            Force Step
          </button>
        </div>

        {/* Debug Data */}
        <details className="text-xs">
          <summary className="cursor-pointer font-medium text-gray-700">Raw Debug Data</summary>
          <pre className="mt-2 bg-gray-800 text-green-400 p-2 rounded overflow-x-auto text-xs">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default WorkflowDebugPanel;